import React from 'react';
import TextareaAutosize from 'react-textarea-autosize';

import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import BaseComponent from 'src/components/BaseComponent';
import { popupModel } from 'src/components/complex';

import {
  AudioRecorder,
  deviceIsMobile,
  lang,
  remCss,
  scssVariables as variables
} from 'src/helpers';

import model from './model';
import ChatFileInput from './ChatFileInput';
import ChatTicketPopup from './ChatTicketPopup';
import AlertManager from 'src/components/alert/AlertManager';
import OriginalMessage from './OriginalMessage';
import PreviewLink from './PreviewLink';
import LANG_CONST from 'src/helpers/lang/const';
import { isAllowedFile, isValidFile } from 'src/helpers/fileCheck';
import { MCText } from 'components/basic';

class ChatInput extends BaseComponent {
  recordStream = null;
  recordTool = null;
  recordTimeId = null;
  state = { recording: false, time: 0 };

  componentDidMount() {
    model.subscribe(this.subscription);
    model.on('TICKET_UNAVAILABLE', this.openChatTicketPopup);
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
    model.cancel('TICKET_UNAVAILABLE', this.openChatTicketPopup);
    this.destroyRecord();
  }

  addNewLineToTextArea = () => {
    const { value = '' } = this.props;
    let newValue = value + '\r\n';
    this.props.onChange && this.props.onChange(newValue.toString());
  };

  onKeyPressInput = (event) => {
    console.log(event);
    if (event.key === 'Enter' || event.code === 'Enter') {
      if (event.ctrlKey || deviceIsMobile) return this.addNewLineToTextArea();
      if (model.validMessageLength()) this.props.onSendMessage && this.props.onSendMessage();
      event.preventDefault();
    }
  };
  onKeyDownInput = (event) => {
    if (event.key === 'Enter' || event.code === 'Enter') {
      if (!event.ctrlKey) {
        event.preventDefault();
        return false;
      }
    }
  };

  onChangeInput = (event) => {
    const value = event.target.value;

    this.props.onChange && this.props.onChange(value.toString());
  };

  onDrop = (files) => {
    this.props.onFiles && this.props.onFiles(files);
    this.textInputRef.focus();
  };

  onUpload = async (e) => {
    const files = Object.values(e.target.files);

    const allowedFiles = files.filter(isAllowedFile);
    const contentOk = await Promise.all(allowedFiles.map(isValidFile));
    const filteredFiles = allowedFiles.filter((file, index) => contentOk[index]);
    if (files.length > filteredFiles.length) {
      AlertManager.popup(AlertManager.TYPES.WARN, {
        close: false,
        title: LANG_CONST.validation.file_invalid,
        actionButton: {
          label: LANG_CONST.buttons.ok,
          action: (actions) => actions.hide()
        }
      });
    } else if (filteredFiles.length > 0) {
      popupModel.setActivePopup('chat_file_input', {
        active: true,
        component: (props) => (
          <ChatFileInput {...props} files={filteredFiles} text={this.props.value} />
        )
      });
    }
    e.target.value = [];
  };

  onPaste = async (e) => {
    if (e.clipboardData.files.length) {
      const files = Object.values(e.clipboardData.files);
      const allowedFiles = files.filter(isAllowedFile);
      const contentOk = await Promise.all(allowedFiles.map(isValidFile));
      const filteredFiles = allowedFiles.filter((file, index) => contentOk[index]);

      if (filteredFiles.length > 0) {
        popupModel.setActivePopup('chat_file_input', {
          active: true,
          component: (props) => (
            <ChatFileInput {...props} files={filteredFiles} text={this.props.value} />
          )
        });
      }
    }
  };

  onCancelImage = (file) => {
    this.props.onRemoveFiles && this.props.onRemoveFiles([file]);
    this.textInputRef.focus();
  };

  toggleAudioRecording = () => {
    this.recording ? this.stopAudioRecord() : this.startAudioRecord();
  };

  toggleVideoRecording = () => {
    this.recording ? this.stopVideoRecord() : this.startVideoRecord();
  };

  getMinSecs = (value) => {
    let result = '00:00';
    if (value > 0) {
      let mins = Math.floor(value / 60);
      if (mins.toString().length === 1) mins = '0' + mins;
      let secs = Math.floor(value - mins * 60);
      if (secs.toString().length === 1) secs = '0' + secs;
      result = mins + ':' + secs;
    }
    return result;
  };

  getStream = async (video) => {
    try {
      if (navigator.mediaDevices) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: !!video });
        if (stream && this.recording) {
          this.recording.stream = stream;
          if (video && this.cameraVideo) this.cameraVideo.srcObject = stream;
          this.recording.mediaRecorder = new MediaRecorder(stream);
          this.recording.chunks = [];
          this.recording.mediaRecorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0 && this.recording && this.recording.chunks) {
              this.recording.chunks.push(e.data);
              if (!this.recording.started) this.recording.started = Date.now();
              this.recording.type = video ? 'video' : 'audio';
              this.recording.duration = Math.floor((Date.now() - this.recording.started) / 1000);
              this.recording.time = this.getMinSecs(this.recording.duration);
              this.setState({
                recording: this.recording,
                recordingMode: video ? 'video' : 'audio',
                test: Date.now()
              });
            }
          };
          return true;
        }
      } else {
        console.log('Media Decives will work only with SSL.....');
      }
    } catch (err) {
      AlertManager.popup(AlertManager.TYPES.WARN, {
        maxWidth: 550,
        close: false,
        title: LANG_CONST.notice.required_mic_access,
        actionButton: {
          label: LANG_CONST.buttons.ok,
          action: ({ hide }) => hide()
        }
      });
      console.debug('getStream err', err);
    }
    return false;
  };

  startVideoRecord = async () => {
    this.recording = {
      type: 'video',
      duration: 0,
      time: '00:00',
      started: false
    };
    let granted = await this.getStream(true);
    if (!granted || !this.recording) {
      this.recording = false;
      this.setState({ recording: false });
      return;
    } else {
      this.setState({ recording: this.recording });
    }
    this.recording.mediaRecorder.start(10);
  };

  stopVideoRecord = async (cancel) => {
    try {
      let recording = this.recording;
      recording && clearInterval(recording.interval);
      if (recording && recording.stream) {
        recording.stream.getAudioTracks().forEach((track) => track.stop());
        recording.stream.getVideoTracks().forEach((track) => track.stop());
        recording.mediaRecorder.stop();
        if (recording.duration >= 2) {
          const blob = new Blob(recording.chunks, { type: 'video/*' });
          let ext = 'mp4';
          let record = {
            counter: 0,
            name: 'record' + ext,
            type: 'video/' + ext,
            fileType: 'video',
            file: blob,
            uri: window.URL.createObjectURL(blob),
            duration: recording.duration,
            peaks: false
          };
          console.log('stopVideoRecord', record);
          this.props.onRecord && this.props.onRecord(record, cancel);
        }
      }
    } catch (err) {
      console.log('stopVideoRecord catch', err);
    }
    this.recording = null;
    this.setState({ recording: false });
  };

  startAudioRecord = async () => {
    this.recording = {
      type: 'audio',
      duration: 0,
      time: '00:00',
      started: false
    };
    this.setState({ recording: this.recording });
    let granted = await this.getStream(false);
    if (!granted || !this.recording) {
      this.recording = false;
      this.setState({ recording: false });
      return;
    }
    this.recording.mediaRecorder.start(10);
  };

  stopAudioRecord = async (cancel = false) => {
    try {
      let recording = this.recording;
      recording && clearInterval(recording.interval);
      if (recording && recording.stream) {
        recording.stream.getAudioTracks().forEach((track) => track.stop());
        recording.mediaRecorder.stop();
        if (recording.duration >= 2) {
          const blob = new Blob(recording.chunks, { type: 'audio/*' });
          let ext = 'wav';
          let record = {
            counter: 0,
            name: 'record' + ext,
            type: 'audio/' + ext,
            fileType: 'audio',
            file: new File([blob], 'record.' + ext, { type: 'audio/wav' }),
            uri: window.URL.createObjectURL(blob),
            duration: recording.duration,
            peaks: false
          };
          console.log('stopAudioRecord', record);
          this.props.onRecord && this.props.onRecord(record, cancel);
        }
      }
    } catch (err) {
      console.log('stopAudioRecord catch', err);
    }
    this.recording = null;
    this.setState({ recording: false });
  };

  sendMessage = () => {
    if (!model.validMessageLength()) return;
    if (!model.isChatAvailable()) return this.openChatTicketPopup();
    this.props.onSendMessage();
  };

  destroyRecord = () => {
    if (this.recordStream) {
      const tracks = this.recordStream.getTracks();
      tracks.forEach((track) => track.stop());
      this.recordStream = null;
      clearInterval(this.recordTimeId);
    }
  };

  record = async () => {
    if (this.recordStream) {
      this.destroyRecord();
      this.setState({ recording: false });
      return;
    }

    this.recordTool = new AudioRecorder(600, {
      stop: () => {
        this.destroyRecord();
        this.setState({ recording: false });
      }
    });
    this.recordStream = await this.recordTool.getUserMedia();
    this.recordTool.startRecording();
    this.setState({ recording: true, time: 0 });
    this.recordTimeId = setInterval(() => {
      this.setState({ time: this.state.time + 1 });
    }, 1000);
  };

  openChatTicketPopup = (options = {}) => {
    let { force } = options;
    let available = model.isChatAvailable();
    if (!available || force) {
      popupModel.setActivePopup('chat_ticket_popup', {
        active: true,
        component: ChatTicketPopup
      });
    }
  };

  render() {
    let {
      value,
      placeholder = '',
      files = [],
      inputRef,
      preview,
      previewUrl,
      previewing,
      onCancelPreview,
      photoOnly,
      linksOnly,
      originalMessage,
      clearSelectedMessage
    } = this.props;
    if (!files) files = [];
    let { recording, recordingMode } = this.state;

    let placeName = files && files.length ? 'Enter to send' : placeholder;

    return (
      <div className='mc-send-box-msg-container' onPaste={this.onPaste}>
        <OriginalMessage
          show={!!originalMessage}
          originalMessage={originalMessage}
          onClose={clearSelectedMessage}
        />
        <PreviewLink
          show={!files?.length && ((!!preview && !!preview?.title) || previewing)}
          previewing={previewing}
          preview={preview}
          onClose={onCancelPreview}
          previewUrl={previewUrl}
        />
        {!model.validMessageLength() && (
          <MCText
            label={LANG_CONST.notice.limit_reached}
            primary
            style={{ marginBottom: remCss(10) }}
          />
        )}
        <div className='mc-send-box-msg'>
          <div className='mc-send-box-msg-input'>
            {recording ? (
              <div className={'mc-grow mc-recoding-time'}>
                <button
                  className={'mc-no-style-btn'}
                  onClick={() =>
                    recordingMode === 'audio'
                      ? this.stopAudioRecord(true)
                      : this.stopVideoRecord(true)
                  }
                >
                  <SVGIcon
                    iconname={BASE_ICONS.DELETE}
                    fill={variables['colors-mc-danger']}
                    width={30}
                  />
                </button>
                <SVGIcon iconname={'recording-icon'} />
                {recording.time}
              </div>
            ) : (
              <>
                <TextareaAutosize
                  disabled={(photoOnly && !linksOnly) || recording}
                  autoFocus
                  ref={(ref) => {
                    inputRef && inputRef(ref);
                    this.textInputRef = ref;
                  }}
                  className='mc-chat-input'
                  type='text'
                  placeholder={!recording ? placeName : recording.time}
                  onKeyUp={this.onKeyPressInput}
                  onKeyDown={this.onKeyDownInput}
                  onChange={this.onChangeInput}
                  onFocus={this.props.onInputMsgFocus}
                  value={!recording ? value : ''}
                  maxRows={3}
                  minRows={1}
                  style={{ overflow: value ? 'auto' : 'hidden', resize: 'none' }}
                />
              </>
            )}
            <button type='button' className={'mc-no-style-btn'} onClick={() => this.file?.click()}>
              <SVGIcon
                iconname={BASE_ICONS.PAPERCLIP}
                width={34}
                fill={variables['colors-mc-gray5']}
              />
            </button>
            <input
              type={'file'}
              multiple={true}
              ref={(r) => (this.file = r)}
              onChange={this.onUpload}
              hidden
            />
            <video
              className={`mc-camera-video ${
                recording && recording.stream && recordingMode === 'video' ? ' active' : ''
              }`}
              muted
              ref={(ref) => (this.cameraVideo = ref)}
              width={200}
              autoPlay
            />
          </div>

          {!recording && !!value ? (
            <div className={'mc-no-style-btn'} onClick={this.sendMessage}>
              <SVGIcon
                iconname={BASE_ICONS.SEND}
                width={50}
                circle
                light
                padding={5}
                fill={variables['colors-mc-primary']}
              />
            </div>
          ) : (
            <div className={'mc-send-box-msg-btn'} onClick={this.toggleAudioRecording}>
              <SVGIcon
                iconname={BASE_ICONS.MIC}
                width={recording ? 38 : 34}
                fill={
                  recording && recordingMode === 'audio'
                    ? variables['colors-mc-primary']
                    : variables['colors-mc-gray5']
                }
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ChatInput;
